'use client';

import {
  Button,
  Card,
  Heading,
  Text,
  Flex,
  Pill,
  ProgressBar,
  Separator,
} from '@/_components';
import { useContext, useMemo, type FC } from 'react';
import { UserContext } from '@/_contexts/UserProvider';
import { getExpiredDaysAgo } from '@/_utils/dates';
import Link from 'next/link';
import type { QuestionnaireCardProps } from '@/_components/QuestionnaireCard/QuestionnaireCard.types';
import { UserPreviewContext } from '@/_contexts/admin/UserPreviewProvider';
import { getAdminDocumentDropOffPreviewLink } from '@/_utils/admin';
import styles from './EnhancedQuestionnaireCard.module.css';

/* This is to replace the QuestionnaireCard.tsx, most of these logics are just a copy-paste from there */
export const EnhancedQuestionnaireCard: FC<QuestionnaireCardProps> = ({
  customStatus,
  description,
  dueDate,
  isPreview,
  questionnaire = { id: undefined, document_drop_off_enabled: false },
  sections = [],
  status,
  title,
}) => {
  const {
    selectedCompanyHash,
    selectedCompanyId,
    isUserReadOnly,
    isCompanyReadOnly,
  } = useContext(UserContext);
  const { previewCompany, isPreviewLoading } = useContext(UserPreviewContext);

  const { id: questionnaireId, document_drop_off_enabled: isDocumentDropOff } =
    questionnaire;

  // Questionnaire statuses
  const isCompleted: boolean = status === 'completed';
  const isLocked: boolean = status === 'locked';
  const isInProgress: boolean = status === 'in_progress';
  const isOpened: boolean = status === 'opened' && !isCompleted;

  // Due date statuses
  const dayDifference: number = getExpiredDaysAgo(dueDate);
  const dayDifferenceCount: number = Math.abs(getExpiredDaysAgo(dueDate));
  const hasDueDate: boolean =
    !Number.isNaN(dayDifferenceCount) && !isLocked && !isCompleted;
  const isDueToday: boolean =
    !isCompleted && hasDueDate && dayDifferenceCount <= 1;
  const isExpired: boolean = !isCompleted && hasDueDate && dayDifference > 0;

  const isViewOnly: boolean =
    isLocked || isCompleted || isUserReadOnly || isCompanyReadOnly;

  const questionnaireLink: string = useMemo(
    () =>
      isDocumentDropOff
        ? `/document-drop-off?questionnaireId=${questionnaireId}&companyId=${selectedCompanyId}`
        : `/questionnaire/${selectedCompanyHash}/?questionnaireId=${questionnaireId}`,
    [
      questionnaireId,
      isDocumentDropOff,
      selectedCompanyId,
      selectedCompanyHash,
    ],
  );

  const questionnaireAdminLink: string = useMemo(
    () =>
      typeof previewCompany?.id === 'number'
        ? isDocumentDropOff
          ? getAdminDocumentDropOffPreviewLink(
              questionnaireId,
              previewCompany?.id,
            ) || ''
          : `/preview/${previewCompany?.id}/questionnaire/${questionnaireId}`
        : '',
    [previewCompany?.id, questionnaireId, isDocumentDropOff],
  );

  // Calculate progress
  const progress = useMemo(() => {
    if (!sections.length) return 0;
    if (isCompleted) return 100;

    const completedSections = sections.filter(
      (section) => section.isCompleted,
    ).length;
    return Math.round((completedSections / sections.length) * 100);
  }, [sections, isCompleted]);

  const getStatusPills = () => {
    const pills = [];

    pills.push(
      <Pill
        key="questionnaire"
        iconLeft="circles-three-plus"
        label="Questionnaire"
        intent="default"
        size="small"
      />,
    );

    // Due date status pills
    if (isExpired) {
      pills.push(
        <Pill
          key="overdue"
          label="Overdue"
          intent="danger"
          iconLeft="warning-circle"
        />,
      );
    } else if (isDueToday) {
      pills.push(
        <Pill
          key="due-today"
          label="Due Tomorrow"
          intent="warning"
          iconLeft="warning"
        />,
      );
    } else if (hasDueDate && dayDifferenceCount <= 7) {
      pills.push(
        <Pill
          key="due-soon"
          label={`Due in ${dayDifferenceCount} days`}
          intent="warning"
          iconLeft="warning"
        />,
      );
    } else if (hasDueDate) {
      pills.push(
        <Pill
          key="due"
          label={`Due in ${dayDifferenceCount} days`}
          intent="default"
          iconLeft="info"
        />,
      );
    }

    // Progress status pills
    if (isInProgress) {
      pills.push(
        <Pill
          key="in-progress"
          label="In Progress"
          intent="info"
          iconLeft="clock"
        />,
      );
    } else if (isCompleted) {
      pills.push(
        <Pill
          key="completed"
          label="Completed"
          intent="success"
          iconLeft="check-circle-filled"
        />,
      );
    } else if (isLocked) {
      pills.push(
        <Pill key="locked" label="Locked" intent="secondary" iconLeft="lock" />,
      );
    }

    if (customStatus) {
      pills.push(
        <Pill
          key="custom"
          label={customStatus}
          intent="default"
          iconLeft="info"
        />,
      );
    }

    return pills;
  };

  const getNextSection = () => {
    const nextSection = sections.find((section) => !section.isCompleted);
    return nextSection?.name || 'Introduction';
  };

  const getProgressText = () => {
    if (isCompleted) return 'No actions required';
    if (isLocked) return 'Locked';

    const completedCount = sections.filter((s) => s.isCompleted).length;
    const totalCount = sections.length;
    return `${completedCount}/${totalCount}. Next: ${getNextSection()}`;
  };

  const getCardStatus = () => {
    if (isExpired) return 'overdue';
    if (isDueToday || (hasDueDate && dayDifferenceCount <= 7)) {
      return 'due-soon';
    }
    if (isCompleted) return 'completed';
    return 'default';
  };

  const getProgressIntent = () => {
    if (isCompleted) return 'success';
    return 'info';
  };

  return (
    <Card className={styles.enhancedCard} data-status={getCardStatus()}>
      <Flex isColumn={true} gap={10} padding={8}>
        <Flex isYCentered={true} gap={4}>
          {getStatusPills()}
        </Flex>

        <Flex isColumn={true} gap={4}>
          <Heading level={2} color="primary" breakWords={true}>
            {title}
          </Heading>
          {!isCompleted && (
            <Text type="small" color="secondary">
              {description}
            </Text>
          )}
        </Flex>

        <Separator />

        <Flex isBetween={true} isYCentered={true}>
          <Flex isColumn={true} gap={2} style={{ flex: 1 }}>
            <Text type="small" color={isCompleted ? 'success' : 'primary'}>
              {getProgressText()}
            </Text>
            <ProgressBar
              value={progress}
              max={100}
              intent={getProgressIntent()}
              className={styles.enhancedProgressBar}
            />
          </Flex>
          {isPreview ? (
            <Link
              href={questionnaireAdminLink}
              tabIndex={-1}
              aria-disabled={!questionnaireAdminLink}
            >
              <Button
                value="Preview Questionnaire"
                intent="secondary"
                isDisabled={!questionnaireAdminLink}
                isLoading={isPreviewLoading}
                iconRight="eye"
              />
            </Link>
          ) : (
            <Link href={questionnaireLink} tabIndex={-1}>
              <Button
                value={isViewOnly ? 'View' : isOpened ? 'Continue' : 'Start'}
                intent={isViewOnly ? 'secondary' : 'primary'}
                iconRight={isViewOnly ? 'eye' : 'arrow-right'}
                data-testid={
                  isOpened
                    ? 'ClientDashboard-questionnaire-continue-button'
                    : 'ClientDashboard-questionnaire-start-button'
                }
              />
            </Link>
          )}
        </Flex>
      </Flex>
    </Card>
  );
};
